import { useEffect, useMemo, useState } from 'react';

import useNavigateWithTransition from '@/hooks/useNavigateWithTransition';
import GalleryService from '@/services/gallery.service';
import CardHeader from '@/shared-components/cards/components/CardHeader';
import BoardContentRenderer from '@/shared-components/forms/add-board-content/board-text-body/BoardContentRenderer';
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';
import {
  hydratePollsWithPersistedVotes,
  persistPollVote,
} from '@/utils/pollPersistence';
import {
  hydrateQuizzesWithPersistedAnswers,
  persistQuizAnswer,
} from '@/utils/quizPersistence';
import {
  resolveUserProfileImage,
  resolveUserProfileTypeWithFallback,
} from '@/utils/userProfileImage';

import './styles/PostListItem.css';

export default function PostListItem({ data }) {

  const navigateWithTransition = useNavigateWithTransition();
  const [polls, setPolls] = useState(() =>
    hydratePollsWithPersistedVotes(data._id, data.post_container.post_polls || [])
  );
  const [quizzes, setQuizzes] = useState(() =>
    hydrateQuizzesWithPersistedAnswers(data._id, data.post_container.post_quizzes || [])
  );
  // unwrap wrapper if present
  const item = data.post || data;

  // 2b) re-seed polls/quizzes whenever this PostListItem’s `data` changes
  useEffect(() => {
    setPolls(
      hydratePollsWithPersistedVotes(data._id, data.post_container.post_polls || [])
    );
  }, [data._id, data.post_container.post_polls]);
  useEffect(() => {
    setQuizzes(
      hydrateQuizzesWithPersistedAnswers(data._id, data.post_container.post_quizzes || [])
    );
  }, [data._id, data.post_container.post_quizzes]);

  const asObject = (value) => (value && typeof value === 'object' ? value : null);

  // guard state
  // ——— Author info — pick whichever owner exists ———
  const ownerCandidate = asObject(data.owner)
    || asObject(item.owner)
    || asObject(data.gallery)
    || asObject(data.artist)
    || asObject(data.collector)
    || asObject(data.post_creator)
    || asObject(data.post_owner)
    || asObject(item.gallery)
    || asObject(item.artist)
    || asObject(item.collector)
    || asObject(item.post_creator)
    || asObject(item.post_owner)
    || null;
  const avatarId = resolveUserProfileImage(
    ownerCandidate?.user_image,
    ownerCandidate?.image,
    ownerCandidate?.owner_image,
    ownerCandidate?.image_id,
    data.user_image,
    item.user_image,
    data.user_image_id,
    item.user_image_id,
  );
  const username = ownerCandidate?.user_username
    ?? ownerCandidate?.username
    ?? ownerCandidate?.owner_username
    ?? data.user_username
    ?? item.user_username
    ?? null;
  const ownerProfileType = resolveUserProfileTypeWithFallback(
    'collector',
    ownerCandidate,
    data.user_type,
    item.user_type,
  );
  const createdAtValue = data.createdAt || item.createdAt;
  const timeAgo = createdAtValue
    ? Utils.elapsedTimeOrShortDate(new Date(createdAtValue).getTime(), 7)
    : '';

  // ——— Build lookup maps from data.post_container ———
  const textsById = useMemo(() =>
    Object.fromEntries(data.post_container.post_texts.map(t => [t.textId, t])),
  [data.post_container.post_texts]);

  const pollsById    = useMemo(() => Object.fromEntries(
    polls.map(p => [Number(p.pollId), p])
  ), [polls]);

  const quizzesById  = useMemo(() => Object.fromEntries(
    quizzes.map(q => [Number(q.quizId), q])),
  [quizzes]);

  const picturesById = useMemo(
    () => Object.fromEntries(data.post_container.post_pictures.map(q => [q.groupId, q])),
    [data.post_container.post_pictures]
  );

  const artworksById = useMemo(
    () => Object.fromEntries(data.post_container.post_artworks.map(q => [q.groupId, q])),
    [data.post_container.post_artworks]
  );
  const videosById = useMemo(
    () => Object.fromEntries((data.post_container.post_videos || []).map(q => [q.groupId, q])),
    [data.post_container.post_videos]
  );

  // ——— Vote casting logic ———
  const castVote = async (pollId, optionIndex) => {
    const updated = await GalleryService.castPollVote(data._id, pollId, optionIndex);
    const withVote = {
      ...updated,
      hasVoted: true,
      userAnswer: optionIndex,
    };
    persistPollVote(data._id, pollId, optionIndex);
    setPolls(ps => ps.map(p => (p.pollId === pollId ? withVote : p)));
    return withVote.options;
  };

  // ——— Answer‐casting logic ———
  const castAnswer = async (quizId, answerIndex) => {
    // throws on error or duplicate
    await GalleryService.castQuizAnswer(data._id, quizId, answerIndex);
    // mark that quiz as answered so UI locks
    setQuizzes(qs => 
      qs.map(q => 
        q.quizId === quizId 
          ? { ...q, hasAnswered: true, userAnswer: answerIndex } 
          : q
      )
    );
    persistQuizAnswer(data._id, quizId, answerIndex);
  };

  // ——— 1) Determine "trailer" type for this post ———
  const hasMedia    =
      data.post_container.post_pictures.length > 0 ||
      data.post_container.post_artworks.length > 0 ||
      (data.post_container.post_videos || []).length > 0;
      
  // TODO: use the hasText from other places
  const hasText     = data.post_container.post_texts.some(t => t.content.trim());
  const hasPollQuiz = (data.post_container.post_polls.length > 0)
                   || (data.post_container.post_quizzes.length > 0);

  let trailerPostType;
  if (hasMedia && !hasText && !hasPollQuiz) {
    trailerPostType = 'trailer-media-only';        // Type 1
  } else if (hasMedia && hasText) {
    trailerPostType = 'trailer-media-text';        // Type 2
  } else if (!hasMedia && hasText && hasPollQuiz) {
    trailerPostType = 'trailer-text-poll';         // Type 3
  } else if (!hasMedia && !hasText && hasPollQuiz) {
    // *** brand-new poll/quiz only case ***
    trailerPostType = 'trailer-poll-quiz-only';  // Type 4
  } else {
    trailerPostType = 'trailer-text-only';   // Type 5
  }

  const isPollQuizOnly = trailerPostType === 'trailer-poll-quiz-only';

  // ——— 2) Always pull out media blocks first ———
  const { post_structure } = data.post_container;
  
  // --- handle card click with snapshot/replace/push ---
  function handleCardClick(e) {
    // don’t navigate if they clicked on a poll/quiz control or on an inline tag link
    if (e.target.closest('input, label, button, video, .post-video-block, .submitted-poll-option-input, .quiz-option, .tag-display')) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    navigateWithTransition(buildPublicContentPath(item, 'post'), {
      state: { from: true },
    });
  }

  return (
    <div
      className={`post-list-item__container ${trailerPostType}`}
      style={{ cursor: 'pointer' }}
      onClick={handleCardClick}
    >
      {/* ——— Header ——— */}
      <CardHeader
        image={avatarId}
        profileType={ownerProfileType}
        username={username}
        timeAgo={timeAgo}
        onUserClick={() => {
          if (!username) return;
          navigateWithTransition(`/user/${username}`);
        }}
      />

      {/* ——— Body: preview, "more", fallback images ——— */}
      <div
        className={[
          'post-list-item__body',
          isPollQuizOnly && 'post-list-item__body--expanded',
        ].filter(Boolean).join(' ')}
      >
        {/* ——— Rich Content Preview ——— */}
        <div
          className="post-list-item__renderer-wrapper" 
        >
          <BoardContentRenderer
            structure={post_structure}
            postId={data._id}
            textsById={textsById}
            picturesById={picturesById}
            artworksById={artworksById}
            videosById={videosById}
            pollsById={pollsById}
            quizzesById={quizzesById}
            onVote={castVote}
            onAnswer={castAnswer}
            previewMode={true}
            interactive={true}
            postMode={true}
          />
        </div>
      </div>
    </div>
  );
}
